import { useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { fallbackFeatureFlags, type FeatureFlagKeys } from "./fallbackFeatureFlags";

const useFeatureFlagsWithFallback = () => {
  const [flags, setFlags] = useState<Record<FeatureFlagKeys, boolean>>(fallbackFeatureFlags);
  const posthog = usePostHog();
  // If the public PostHog key is not set, then fallback feature flags are being used.
  const isFallbackFeatureFlagsEnabled = !import.meta.env.VITE_PUBLIC_POSTHOG_KEY;

  useEffect(() => {
    let posthogCallback: (() => void) | undefined;
    if (!isFallbackFeatureFlagsEnabled) {
      // Register callback for future updates when flags load/change.
      posthogCallback = posthog.onFeatureFlags((_featureFlags, flagVariants, context) => {
        // If feature flags errored, log and notify
        if (context?.errorsLoading) {
          console.log("PostHog feature flags errored", context.errorsLoading);
          setFlags((prev) => (JSON.stringify(prev) === JSON.stringify(fallbackFeatureFlags) ? prev : fallbackFeatureFlags));
        } else {
          // Only update state if flags actually changed. PostHog reloads flags
          // after every identify() call; without this check the new object
          // reference triggers a re-render cascade through every component that
          // calls useFeatureFlags(), including Router — which remounts route
          // elements and causes duplicate API calls.
          setFlags((prev) => {
            const next = flagVariants as Record<FeatureFlagKeys, boolean>;
            return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
          });
        }
      });
    }
    return () => {
      if (typeof posthogCallback === "function") {
        posthogCallback();
      }
    };
  }, []);

  const getFeatureFlag = (key: FeatureFlagKeys) => {
    // If fallback flags are explicitly enabled, use them.
    if (isFallbackFeatureFlagsEnabled) {
      return fallbackFeatureFlags[key];
    }

    // Use direct check for immediate value (for synchronous usage).
    const flagValue = posthog.isFeatureEnabled(key);

    // If flag is nullish, fall back to default.
    return flagValue ?? flags[key];
  };

  return { getFeatureFlag };
};

export { useFeatureFlagsWithFallback };
